import { cloneElement, useId, type ButtonHTMLAttributes, type ReactElement, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-accent-ink hover:bg-accent-strong shadow-[inset_0_1px_0_rgb(255_255_255/0.35)] disabled:bg-raised disabled:text-fg-subtle disabled:opacity-100 disabled:shadow-none',
  secondary: 'bg-elevated text-fg hover:bg-raised ring-1 ring-inset ring-line',
  ghost: 'text-fg-muted hover:text-fg hover:bg-elevated',
  danger: 'text-danger hover:bg-danger/10',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[13px] gap-1.5 rounded-lg',
  md: 'h-10 px-4 text-sm gap-2 rounded-xl',
  lg: 'h-12 px-6 text-[15px] gap-2.5 rounded-2xl',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly icon?: ReactNode;
}

/** Button styling, for links that act as buttons (e.g. a download). */
export function buttonClass(variant: ButtonVariant = 'secondary', size: ButtonSize = 'md', className?: string): string {
  return cn(
    'inline-flex shrink-0 items-center justify-center font-medium tracking-[-0.01em] whitespace-nowrap transition-[background,color,transform,box-shadow] duration-150 ease-out-soft active:scale-[0.98] disabled:pointer-events-none disabled:opacity-40',
    BUTTON_VARIANTS[variant],
    BUTTON_SIZES[size],
    className,
  );
}

export function Button({ variant = 'secondary', size = 'md', icon, className, children, type = 'button', ...rest }: ButtonProps) {
  return (
    <button type={type} className={buttonClass(variant, size, className)} {...rest}>
      {icon}
      {children}
    </button>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly label: string;
  readonly active?: boolean;
}

export function IconButton({ label, active, className, children, type = 'button', ...rest }: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex size-9 shrink-0 items-center justify-center rounded-xl transition-colors duration-150 disabled:opacity-40',
        active ? 'bg-elevated text-fg' : 'text-fg-muted hover:bg-elevated hover:text-fg',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export interface SegmentedOption<T extends string> {
  readonly value: T;
  readonly label: ReactNode;
  readonly hint?: string;
  /** The option's name for assistive technology when its label is a picture (a glyph, a sample). */
  readonly name?: string;
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  size = 'md',
}: {
  readonly value: T;
  readonly options: ReadonlyArray<SegmentedOption<T>>;
  readonly onChange: (value: T) => void;
  readonly label: string;
  readonly size?: 'sm' | 'md';
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex rounded-xl bg-elevated p-1 ring-1 ring-inset ring-line">
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={option.name}
            title={option.hint}
            onClick={() => onChange(option.value)}
            className={cn(
              'min-w-0 flex-1 truncate rounded-lg font-medium whitespace-nowrap transition-all duration-200 ease-out-soft',
              size === 'sm' ? 'h-7 px-2 text-xs' : 'h-8 px-2 text-[13px] sm:px-3',
              selected ? 'bg-panel text-fg shadow-[0_1px_2px_rgb(0_0_0/0.12),0_0_0_1px_var(--line)]' : 'text-fg-muted hover:text-fg',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** The bare switch control; label it with `aria-label` or a `<label htmlFor>`. */
export function SwitchButton({
  checked,
  onChange,
  disabled,
  id,
  label,
}: {
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly disabled?: boolean;
  readonly id?: string;
  /** Accessible name when no visible `<label>` points at it. */
  readonly label?: string;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative mt-0.5 h-6 w-10 shrink-0 rounded-full transition-colors duration-200 ease-out-soft',
        checked ? 'bg-accent' : 'bg-raised ring-1 ring-inset ring-line-strong',
      )}
    >
      <span
        className={cn(
          'absolute top-1 left-1 size-4 rounded-full shadow-sm transition-transform duration-200 ease-out-soft',
          checked ? 'translate-x-4 bg-accent-ink' : 'bg-fg-muted',
        )}
      />
    </button>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly label: ReactNode;
  readonly description?: ReactNode;
  readonly disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className={cn('flex items-start justify-between gap-4', disabled && 'opacity-50')}>
      <label htmlFor={id} className="min-w-0 cursor-pointer select-none">
        <span className="block text-sm font-medium text-fg">{label}</span>
        {description && <span className="mt-0.5 block text-[13px] leading-snug text-fg-muted">{description}</span>}
      </label>
      <SwitchButton id={id} checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  );
}

export function Slider({
  value,
  onChange,
  onCommit,
  label,
  min = 0,
  max = 1,
  step = 0.01,
  format,
}: {
  readonly value: number;
  readonly onChange: (value: number) => void;
  readonly onCommit?: () => void;
  readonly label: string;
  readonly min?: number;
  readonly max?: number;
  readonly step?: number;
  readonly format?: (value: number) => string;
}) {
  const id = useId();
  const percent = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="mb-2 flex items-center justify-between text-[13px]">
        <label htmlFor={id} className="font-medium text-fg-muted">
          {label}
        </label>
        <span className="tabular text-fg-subtle">{format ? format(value) : `${Math.round(percent)}%`}</span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        onPointerUp={onCommit}
        onKeyUp={onCommit}
        className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-raised accent-[var(--accent)] [&::-webkit-slider-thumb]:size-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-fg [&::-webkit-slider-thumb]:shadow-[0_0_0_4px_var(--panel)]"
        style={{ background: `linear-gradient(to right, var(--accent) ${percent}%, var(--raised) ${percent}%)` }}
      />
    </div>
  );
}

export function Section({ title, aside, children, className }: { readonly title?: ReactNode; readonly aside?: ReactNode; readonly children: ReactNode; readonly className?: string }) {
  return (
    <section className={cn('space-y-3', className)}>
      {(title || aside) && (
        <div className="flex items-center justify-between gap-3">
          {title && <h3 className="text-xs font-semibold tracking-[0.06em] text-fg-subtle uppercase">{title}</h3>}
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

/**
 * A row of colour choices: 32px circles ringed `line-strong`; the chosen one
 * gets a 2px Lime Pressed ring with a Panel offset. The colours are content
 * (what goes into the video), not interface chrome.
 */
export function ColorSwatches<T extends string>({
  colors,
  value,
  onChange,
  label,
  nameOf = (color) => color,
}: {
  readonly colors: readonly T[];
  readonly value: string;
  readonly onChange: (color: T) => void;
  readonly label: string;
  readonly nameOf?: (color: T) => string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
      {colors.map((color) => (
        <button
          key={color}
          type="button"
          role="radio"
          aria-checked={value === color}
          aria-label={nameOf(color)}
          title={nameOf(color)}
          onClick={() => onChange(color)}
          className={cn('size-8 rounded-full ring-offset-2 ring-offset-panel transition-shadow duration-150', value === color ? 'ring-2 ring-accent-strong' : 'ring-1 ring-line-strong')}
          style={{ backgroundColor: color }}
        />
      ))}
    </div>
  );
}

/** A choice shown as a tile with a glyph, a name and a one-line hint; part of a `radiogroup`. */
export function OptionTile({ selected, onSelect, icon, label, hint }: { readonly selected: boolean; readonly onSelect: () => void; readonly icon: ReactNode; readonly label: string; readonly hint: string }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'flex h-14 min-w-0 items-center gap-3 rounded-xl px-3 text-left ring-1 transition-colors duration-150 ring-inset',
        selected ? 'bg-accent-soft text-fg ring-accent/60' : 'bg-elevated text-fg-muted ring-line hover:text-fg',
      )}
    >
      {icon}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-fg">{label}</span>
        <span className="block truncate text-xs text-fg-subtle">{hint}</span>
      </span>
    </button>
  );
}

/** A grouped value: a short caption over a tabular figure, on an Elevated tile. */
export function StatTile({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="min-w-0 rounded-xl bg-elevated px-3.5 py-3 ring-1 ring-line ring-inset">
      <p className="truncate text-xs text-fg-subtle">{label}</p>
      <p className="mt-0.5 truncate text-sm font-medium text-fg tabular">{value}</p>
    </div>
  );
}

/** A frame's shape drawn as an outline (`aspect` = width ÷ height), `max` pixels on its long side. */
export function AspectGlyph({ aspect, max = 22, className }: { readonly aspect: number; readonly max?: number; readonly className?: string }) {
  const width = aspect >= 1 ? max : max * aspect;
  const height = aspect >= 1 ? max / aspect : max;
  return (
    <span aria-hidden className={cn('flex shrink-0 items-center justify-center', className)} style={{ width: max + 6, height: max + 6 }}>
      <span className="rounded-[4px] border-[1.5px] border-current" style={{ width, height }} />
    </span>
  );
}

export function ProgressBar({ value, className }: { readonly value: number; readonly className?: string }) {
  const clamped = Math.min(1, Math.max(0, value));
  return (
    <div className={cn('h-1.5 overflow-hidden rounded-full bg-raised', className)} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(clamped * 100)}>
      <div className="h-full rounded-full bg-accent transition-[width] duration-300 ease-out-soft" style={{ width: `${clamped * 100}%` }} />
    </div>
  );
}

export function Spinner({ className }: { readonly className?: string }) {
  return <span className={cn('inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent opacity-70', className)} aria-hidden />;
}

export function Notice({ tone = 'neutral', icon, children }: { readonly tone?: 'neutral' | 'accent' | 'danger'; readonly icon?: ReactNode; readonly children: ReactNode }) {
  return (
    <div
      className={cn(
        'flex gap-2.5 rounded-xl px-3.5 py-3 text-[13px] leading-snug',
        tone === 'neutral' && 'bg-elevated text-fg-muted',
        tone === 'accent' && 'bg-accent-soft text-fg',
        tone === 'danger' && 'bg-danger/10 text-danger',
      )}
    >
      {icon && <span className="mt-px shrink-0">{icon}</span>}
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/**
 * Explains a control in plain words on hover or keyboard focus (e.g. what an
 * SRT file is). Touch screens have no hover, so pair it with visible copy
 * where the explanation matters there too (`pointer-coarse:`).
 */
export function Tooltip({
  content,
  children,
  side = 'bottom',
  align = 'start',
}: {
  readonly content: ReactNode;
  readonly children: ReactElement<{ 'aria-describedby'?: string }>;
  /** Below by default: inspector panels scroll, and a tip above the first row would be clipped. */
  readonly side?: 'top' | 'bottom';
  readonly align?: 'start' | 'center' | 'end';
}) {
  const id = useId();
  return (
    <span className="group/tip relative inline-flex">
      {cloneElement(children, { 'aria-describedby': id })}
      <span
        id={id}
        role="tooltip"
        className={cn(
          'pointer-events-none absolute z-30 w-max max-w-64 rounded-lg bg-fg px-2.5 py-1.5 text-xs leading-snug font-normal text-bg opacity-0 shadow-lg transition-[opacity,translate] duration-150 ease-out-soft',
          'group-focus-within/tip:translate-y-0 group-focus-within/tip:opacity-100 group-hover/tip:translate-y-0 group-hover/tip:opacity-100 group-hover/tip:delay-300',
          side === 'bottom' ? 'top-full mt-2 -translate-y-1' : 'bottom-full mb-2 translate-y-1',
          align === 'start' && 'left-0',
          align === 'center' && 'left-1/2 -translate-x-1/2',
          align === 'end' && 'right-0',
        )}
      >
        {content}
      </span>
    </span>
  );
}
